export const healthPageData = {
  stats: [
    { value: "25%", label: "Procurement Cost Savings" },
    { value: "80%", label: "Process Automation" },
    { value: "99%+", label: "Supplier Compliance" },
    { value: "2.5h", label: "Staff Time Saved Daily" },
  ],
  statsAccent: "from-blue-600 to-teal-600",

  challengeTitle: "Healthcare Procurement Failures Put Patients and Budgets at Risk",
  challengeSubtitle:
    "Manual processes, regulatory complexity, and fragmented supplier data create costly gaps in healthcare procurement.",
  challenges: [
    {
      icon: "⏰",
      title: "3 Hours of Manual Work Daily",
      description:
        "Staff spend 2–3 hours every day on manual order placements, supplier follow-ups, and status checks — time that should be spent on patient care.",
    },
    {
      icon: "💊",
      title: "Critical Medicine Stockouts",
      description:
        "Reactive replenishment means essential medicines and supplies run out before new orders arrive — putting patients at risk.",
    },
    {
      icon: "📋",
      title: "Compliance Complexity",
      description:
        "Manually tracking GMP, ISO, FDA, and other healthcare supplier certifications is error-prone and audit-risky.",
    },
    {
      icon: "💰",
      title: "Budget Overruns",
      description:
        "Without data-driven spend analysis, healthcare organisations consistently overpay and miss savings opportunities.",
    },
    {
      icon: "🔍",
      title: "Zero Supply Chain Visibility",
      description:
        "No real-time view of supplier performance or potential shortages means disruptions hit without warning.",
    },
    {
      icon: "🌿",
      title: "Unsustainable Sourcing",
      description:
        "Inability to trace product sources makes ESG reporting and sustainable procurement nearly impossible.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-blue-50 text-blue-700 border-blue-100",
    solutionIconBg: "bg-blue-50",
    solutionIconColor: "text-blue-600",
    solutionAccent: "from-blue-600 to-teal-600",
  },

  solutionTitle: "AI-Powered Procurement Built for Healthcare Precision",
  solutionSubtitle:
    "Automate compliance, eliminate stockouts, and reduce costs — while freeing clinical staff from procurement burdens.",
  solutions: [
    {
      icon: "🤖",
      title: "AI Process Automation",
      description:
        "Automate order placement, approvals, and status tracking — eliminating 80% of manual procurement work instantly.",
    },
    {
      icon: "🔮",
      title: "Predictive Inventory Intelligence",
      description:
        "AI forecasts demand for every medicine and supply, triggering automatic reorders before stockouts occur.",
    },
    {
      icon: "✅",
      title: "Automated Compliance Monitoring",
      description:
        "Continuous monitoring of GMP, ISO, FDA, and other supplier certifications with instant alerts for any risk.",
    },
    {
      icon: "⭐",
      title: "Intelligent Supplier Scoring",
      description:
        "Every supplier rated on quality, delivery, and compliance — so you always source from the safest, most reliable vendors.",
    },
    {
      icon: "🚨",
      title: "Real-Time Disruption Alerts",
      description:
        "AI monitors your supply chain 24/7 and alerts you to potential disruptions with contingency recommendations.",
    },
    {
      icon: "💰",
      title: "Budget Optimisation Engine",
      description:
        "AI-driven procurement recommendations maximise ROI within tight healthcare budgets — saving an average of 25%.",
    },
    {
      icon: "🌿",
      title: "End-to-End Traceability",
      description:
        "Full product source tracing ensures sustainability standards and gives you audit-ready ESG documentation.",
    },
    {
      icon: "📊",
      title: "360° Procurement Dashboard",
      description:
        "All procurement data, supplier performance, and spend analysis in one real-time view — for every stakeholder.",
    },
    {
      icon: "🔌",
      title: "EMR & ERP Integration",
      description:
        "Integrates with major hospital EMR, ERP, and inventory systems for seamless, unified data management.",
    },
  ],

  howItWorksTitle: "From Manual Chaos to AI-Powered Precision",
  howItWorksSteps: [
    {
      icon: "🔗",
      title: "Connect Systems",
      description: "Integrate EkkBaz AI with your existing EMR, ERP, and supplier databases via pre-built connectors.",
    },
    {
      icon: "📊",
      title: "AI Analyses Patterns",
      description: "The AI engine learns your consumption patterns, supplier performance, and compliance requirements.",
    },
    {
      icon: "🤖",
      title: "Automation Activates",
      description: "Ordering, compliance checks, and supplier communications are automated from day one.",
    },
    {
      icon: "📉",
      title: "Costs & Risks Decline",
      description: "Monitor cost savings, compliance rates, and stockout prevention improving continuously.",
    },
  ],

  outcomeTitle: "Healthcare Procurement Outcomes That Save Lives and Budgets",
  outcomeSubtitle: "Real results from healthcare organisations using EkkBaz AI.",
  outcomes: [
    {
      value: "25%",
      metric: "Procurement Cost Savings",
      description:
        "AI-driven supplier selection, spend analytics, and automated negotiation save healthcare organisations 25% on total procurement costs.",
    },
    {
      value: "80%",
      metric: "Process Automation",
      description:
        "Manual procurement work reduced by 80% — freeing clinical and administrative staff to focus on patient care.",
    },
    {
      value: "99%+",
      metric: "Supplier Compliance",
      description:
        "Automated compliance monitoring maintains 99%+ supplier regulatory compliance — keeping you audit-ready at all times.",
    },
  ],
  outcomeImage: "/assets/images/medicine5b.png",

  ctaTitle: "Reduce Healthcare Procurement Costs by 25% in 90 Days",
  ctaSubtitle:
    "Book a personalised consultation to see exactly how EkkBaz AI can transform your healthcare procurement operations.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const healthFacilites = [
  {
    id: 1,
    extraLabel: "Your Challenges",
    userType: "Critical Procurement Challenges in Healthcare",
    description: {
      line1: `<strong>Inefficient Manual Processes</strong> — Staff spend 2–3 hours daily on manual order placements, status checks, and supplier follow-ups that could be fully automated.`,
      line2: `<strong>Fragmented Supplier Management</strong> — Managing hundreds of products across dozens of suppliers with no standardized, product-level vendor tracking.`,
      line3: `<strong>Inventory Disruptions</strong> — Frequent stockouts of critical medicines and overstock of slow-moving items drain working capital and endanger patient care.`,
      line4: `<strong>Supply Chain Blind Spots</strong> — No real-time visibility into potential disruptions, leaving procurement teams scrambling when shortages occur.`,
      line5: `<strong>Budget Pressures</strong> — Difficulty balancing cost savings with quality requirements under tight operating budgets.`,
      line6: `<strong>Compliance Complexity</strong> — Ensuring all suppliers meet healthcare regulations (GMP, ISO, FDA) is time-consuming and error-prone manually.`,
      line7: `<strong>Sustainability Gaps</strong> — Inability to trace product sources makes it impossible to meet ESG and sustainable procurement mandates.`,
    },
    banner: "/assets/images/medicine6b.jpg",
  },
  {
    id: 2,
    extraLabel: "Our Solutions",
    userType: "How EkkBaz AI Transforms Healthcare Procurement",
    description: {
      line1: `<strong>AI-Powered Process Automation</strong> — Automate order placement, approvals, and status tracking to boost procurement efficiency by up to 80%.`,
      line2: `<strong>Intelligent Supplier Management</strong> — Standardize vendor management at the product level with performance scoring and automated qualification.`,
      line3: `<strong>Predictive Inventory Intelligence</strong> — AI forecasts demand and triggers reorders automatically, eliminating stockouts and overstock.`,
      line4: `<strong>Real-Time Supply Chain Visibility</strong> — Get instant alerts for potential disruptions with AI-generated contingency recommendations.`,
      line5: `<strong>Optimal Budget Allocation</strong> — Automated procurement recommendations that maximize ROI within budget constraints.`,
      line6: `<strong>Automated Compliance Monitoring</strong> — Continuous supplier compliance checks with instant alerts for any regulatory risks.`,
      line7: `<strong>End-to-End Traceability</strong> — Full product source tracing to ensure sustainability standards and audit readiness.`,
    },
    banner: "/assets/images/medicine5b.png",
  },
  {
    id: 3,
    extraLabel: "Get Started",
    userType: "Reduce Procurement Costs by 25% in 90 Days",
    description: `Healthcare organizations using EkkBaz AI have reduced procurement costs by 25%, cut manual procurement time by 80%, and achieved 99%+ supplier compliance rates. Book a personalized consultation to see exactly how EkkBaz AI can transform your procurement operations.`,
    btnLabel: "Book a Free Consultation",
    formLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
    banner: "/assets/images/medicine3.png",
  },
];

export const healthFAQs = [
  {
    question: "How does EkkBaz AI improve healthcare procurement efficiency?",
    answer:
      "EkkBaz AI automates the entire procurement workflow — from supplier discovery and order placement to invoice management and compliance monitoring — reducing manual procurement time by up to 80% and cutting costs by 25%.",
  },
  {
    question: "How does EkkBaz AI handle healthcare regulatory compliance?",
    answer:
      "The platform continuously monitors supplier compliance with healthcare regulations including GMP, ISO, and FDA standards, and sends automated alerts when any compliance risk is detected — ensuring your procurement stays audit-ready.",
  },
  {
    question: "Can EkkBaz AI prevent medicine stockouts?",
    answer:
      "Yes. EkkBaz AI uses demand forecasting algorithms to predict inventory needs and automatically triggers reorders before stockout thresholds are reached, maintaining optimal stock levels without overstocking.",
  },
  {
    question: "Is EkkBaz AI suitable for small healthcare clinics as well as large hospitals?",
    answer:
      "Absolutely. EkkBaz AI scales from independent pharmacies and clinics to large multi-location hospital networks, with plans and workflows tailored to each organization's size and complexity.",
  },
];
